import React, { useEffect, useState } from 'react'
import {
  GridContainer,
  GridItem,
  DescriptionText,
  HighLighted,
  DescriptionTitle,
  ProcessId,
  ListParticipantsRootArea,
  ElementContainer,
  ArrowDownwardIcon,
  Button,
  Link,
  DownloadLink,
  ErrorContainer,
  DataTableContainer,
  ErrorMessageContainer,
  AlertIcon,
  Card,
  NotFoundImg,
  Tooltip,
  TooltipIcon,
  TooltipText
} from './ListParticipants.styles'
import { useEffectOnce, useViewport, formatDate } from '@chilecompra/react-kit'
import { useNavigate, useParams } from 'react-router-dom'
import { batch, useDispatch, useSelector } from 'react-redux'
import { onGetParticipantsListDownloadFileThunk, onGetParticipantsListThunk } from './ListParticipants.actions'
import { DataTable, Typography } from '@chilecompra/react-kit/components'
import ListParticipantsElementProcess from './ListParticipants.elementProcess'
import ListParticipantsFunctionsDetails from './ListParticipants.functionsDetails'
import { WRITE_ALLOWED_ROLES, FALLBACK_TEXT_TABLE_MESSAGE, PATTERN_DATE } from '../../config/settings/constants'
import { onSetEditModalShowSuccess } from '../EditParticipantModal/EditParticipantModal.actions'
import {
  onGetFunctionByProcessThunk,
  onGetParticipantsListProcessThunk,
  onSetResetSelectedPerson
} from '../ManageParticipants/ManageParticipants.actions'
import { onSetModalShowSuccess, onSetShowSuccessToast } from '../AddParticipantModal/AddParticipantModal.actions'
import ListParticipantsLoading from './ListParticipants.loading'
import { PUBLIC_URL } from '../../config/settings/environment'
import ProtectByOrgCode from '../../components/Security/ProtectByOrgCode'
import { onSetOpenToastSuccess } from '../UserNotificationToast/UserNotificationToast.actions'
import { onSetAutoEditModalShowSuccess } from '../EditAutoParticipantModal/EditAutoParticipantModal.actions'

/**
 * The Participant Container.
 */
const ListParticipantsContainer = () => {
  const navigate = useNavigate()
  const { processId, fromWhere } = useParams()
  const dispatch = useDispatch()
  const { error, loading, participants, externalCode, roleList, participantsValidated, errorOrgCodeValidation } =
    useSelector(state => state.participantsList)
  const {
    authInfo: { roles }
  } = useSelector(state => state.authorizationProvider)
  const { showSuccessToast } = useSelector(state => state.addParticipantModal)
  const [page, setPage] = useState(1)
  const {
    size: { isUpToLarge, isDesktop, isDownToMedium }
  } = useViewport()

  const handleChangePage = (_, page) => setPage(page)

  useEffectOnce(() => {
    batch(() => {
      dispatch(onSetResetSelectedPerson())
      const payload = {
        processNumber: processId,
        fromWhere
      }
      dispatch(onGetParticipantsListThunk(payload))
    })
  })

  const handleGoParticipant = () => {
    dispatch(onSetModalShowSuccess({ showAddParticipantModal: false }))
    navigate(`/participantes/agregar/${fromWhere}/${processId}`)
  }

  const handleClickDownload = () => {
    dispatch(onGetParticipantsListDownloadFileThunk(externalCode))
  }

  useEffect(() => {
    if (showSuccessToast) {
      dispatch(
        onSetOpenToastSuccess({
          open: true,
          message: 'Participante añadido con éxito',
          severity: 'success'
        })
      )
      dispatch(onSetShowSuccessToast({ showSuccessToast: false }))
    }
  }, [showSuccessToast])

  const validateWriteAllowed = roles.includes(WRITE_ALLOWED_ROLES.supervisorBuyer)

  const handleEditParticipant = participantSelected => {
    const {
      fullName,
      role,
      dni,
      email,
      participantProcess: {
        function: { functionId, shortName, name },
        process: { processId, groupName: processName },
        participantProcessId
      },
      isForeignPerson,
      participantId,
      participantDetailId,
      isManual,
      startDate,
      endDate
    } = participantSelected

    if (!validateWriteAllowed) {
      return
    }
    batch(() => {
      dispatch(onGetParticipantsListProcessThunk())
      dispatch(onGetFunctionByProcessThunk(processId))
    })
    return {
      participantSelected,
      fullName,
      role,
      dni,
      email,
      isForeignPerson,
      participantId,
      participantDetailId,
      functionId,
      processId,
      originalProcessId: processId,
      originalFunctionId: functionId,
      startDate,
      endDate,
      isManual,
      processName,
      functionName: shortName || name,
      participantProcessId
    }
  }

  const handleEditTableRow = (participantSelected, manual) => () => {
    const payload = handleEditParticipant(participantSelected)
    dispatch(
      manual === 1
        ? onSetEditModalShowSuccess({ showEditParticipantModal: true, ...payload })
        : onSetAutoEditModalShowSuccess({ showEditAutoParticipantModal: true, ...payload })
    )
  }

  return (
    <ProtectByOrgCode redirectTo="/noAutorizado" validOrgCode={errorOrgCodeValidation}>
      <ListParticipantsRootArea>
        <GridItem xs={12}>
          <GridContainer>
            <GridItem xs={12}>
              <GridItem xs={12}>
                <DescriptionTitle>
                  Participantes del proceso de compra {externalCode && <ProcessId>{externalCode}</ProcessId>}
                  {externalCode && (
                    <Tooltip
                      content={
                        <TooltipText>
                          Este número de identificación lo genera automáticamente Mercado Público. Bajo este número se
                          agrupan el mecanismo de compra, las órdenes de compra que se generen y, si corresponde, el
                          contrato asociado.
                        </TooltipText>
                      }
                    >
                      <TooltipIcon />
                    </Tooltip>
                  )}
                </DescriptionTitle>
              </GridItem>
              <GridItem xs={12}>
                <DescriptionText>
                  Los participantes son todas aquellas personas que desempeñan una función en el proceso de compra o{' '}
                  <br /> contratación. Estas distintas funciones abarcan desde el levantamiento del requerimiento hasta
                  la autorización <br /> de la compra. La información es <HighLighted> privada </HighLighted> y
                  solamente es accesible para Fiscalía, Contraloría y Ministerio Público.
                </DescriptionText>
              </GridItem>
            </GridItem>
            {!error && !loading && participantsValidated && participantsValidated.length > 0 && (
              <GridItem xs={12}>
                <ElementContainer>
                  <GridItem xs={12}>
                    <Typography fontSize={isDownToMedium ? '18px' : '22px'} color="black1" fontWeight="bold">
                      Listado de participantes
                    </Typography>
                  </GridItem>
                  <GridItem xs={12} padding="10px 0 20px 0">
                    <Typography fontSize={isDownToMedium ? '14px' : '16px'} variant="body2">
                      Detalle de las personas que desempeñaron funciones clave dentro del proceso de compra o
                      contratación.
                    </Typography>
                  </GridItem>
                  <GridItem xs={12}>
                    {participantsValidated && participantsValidated.length > 0 && (
                      <DataTableContainer>
                        <DataTable
                          customPagination={{ onChangePage: handleChangePage, page }}
                          headers={[
                            { label: 'Participante', value: 'fullName' },
                            { label: 'Cargo', value: 'role' },
                            { label: 'Identificador', value: 'dni' },
                            { label: 'Correo electrónico', value: 'email' },
                            { label: 'Etapa del proceso', value: 'functionRole' },
                            { label: 'Función', value: 'functionRole' },
                            { label: 'Fecha inicio', value: 'startDate' },
                            { label: 'Fecha fin', value: 'endDate' },
                            { label: 'Acciones', value: 'acciones' }
                          ]}
                          headersCellsProps={[
                            { enableSort: false },
                            { enableSort: false },
                            { enableSort: false },
                            { enableSort: false },
                            { enableSort: false },
                            { enableSort: false },
                            { enableSort: false },
                            { enableSort: false }
                          ]}
                          enableStripped
                          loading={loading}
                          records={participantsValidated}
                          enablePagination
                          initialRows={5}
                          recordsDecorators={[
                            ({ fullName }) => fullName || FALLBACK_TEXT_TABLE_MESSAGE,
                            ({ role }) => role || FALLBACK_TEXT_TABLE_MESSAGE,
                            ({ dni }) => dni || FALLBACK_TEXT_TABLE_MESSAGE,
                            ({ email }) => email || FALLBACK_TEXT_TABLE_MESSAGE,
                            ({ participantProcess }) =>
                              participantProcess?.process?.groupName || FALLBACK_TEXT_TABLE_MESSAGE,
                            ({ participantProcess }) =>
                              participantProcess?.function?.shortName ||
                              participantProcess?.function?.name ||
                              FALLBACK_TEXT_TABLE_MESSAGE,
                            ({ startDate }) =>
                              startDate ? formatDate(startDate, PATTERN_DATE) : FALLBACK_TEXT_TABLE_MESSAGE,
                            ({ endDate }) =>
                              endDate ? formatDate(endDate, PATTERN_DATE) : FALLBACK_TEXT_TABLE_MESSAGE,
                            element => (
                              <Link
                                color={validateWriteAllowed ? 'primary' : 'gray1'}
                                component="button"
                                textDecoration={validateWriteAllowed ? 'underline' : 'none'}
                                onClick={handleEditTableRow(element, element.isManual)}
                              >
                                Editar
                              </Link>
                            )
                          ]}
                        />
                      </DataTableContainer>
                    )}
                  </GridItem>
                  <GridItem xs={12} padding="20px 0 0">
                    <GridContainer>
                      <GridItem xs={12} md={4} lg={4} padding="0 0 10px">
                        {participantsValidated && participantsValidated.length > 0 && (
                          <DownloadLink color="primary" onClick={handleClickDownload}>
                            <ArrowDownwardIcon />
                            Descargar listado en excel
                          </DownloadLink>
                        )}
                      </GridItem>
                      <GridItem xs={2} sm={5} md={5} lg={6} />
                      <GridItem xs={10} sm={7} md={3} lg={2}>
                        {participantsValidated && participantsValidated.length > 0 && (
                          <Button
                            disabled={!validateWriteAllowed}
                            variant="contained"
                            color="primary"
                            onClick={handleGoParticipant}
                          >
                            Añadir participante
                          </Button>
                        )}
                      </GridItem>
                    </GridContainer>
                  </GridItem>
                </ElementContainer>
              </GridItem>
            )}
            {!loading && !error && participantsValidated && participantsValidated.length === 0 && (
              <Card className="not__found" color="blank" padding="24px" enableBorder>
                <GridContainer alignItems="center" direction="column" justifyContent="center">
                  <NotFoundImg alt="Icono de no encontrado" src={`${PUBLIC_URL}/assets/images/nofound.svg`} />

                  <Typography fontWeight="bold" margin="16px 0 0" variant="h4">
                    Ingresa los participantes de tu proceso de compra
                  </Typography>

                  <Typography color="black3" margin="16px 0 24px" variant="body2">
                    Aún no agregas ningún participante
                  </Typography>

                  <Button
                    color="primary"
                    disabled={!validateWriteAllowed}
                    onClick={handleGoParticipant}
                    variant="contained"
                    size="small"
                  >
                    Comenzar a agregar participantes
                  </Button>
                </GridContainer>
              </Card>
            )}
            {!error && !loading && participants && participants.length > 0 && (
              <GridItem padding={isUpToLarge ? '24px 24px 0 0' : '24px 0 0'} xs={12} lg={6}>
                <ElementContainer>
                  <GridItem xs={12}>
                    <ListParticipantsElementProcess data={participants} />
                  </GridItem>
                </ElementContainer>
              </GridItem>
            )}
            {!error && !loading && roleList && roleList.length > 0 && (
              <GridItem padding="24px 0 0" xs={12} lg={6}>
                <ElementContainer>
                  <GridItem xs={12}>
                    <ListParticipantsFunctionsDetails data={roleList} />
                  </GridItem>
                </ElementContainer>
              </GridItem>
            )}
            {error && !loading && (
              <ErrorContainer item container xs={12} justifyContent="center" alignContent="center">
                <GridItem container xs={12} sm={9} md={9} lg={7} justifyContent="center" alignContent="center">
                  <ErrorMessageContainer>
                    <AlertIcon alt="Icono de alerta" src={`${PUBLIC_URL}/assets/images/custom_alert_icon.svg`} />
                    <Typography
                      fontSize="16px"
                      lineHeight="21px"
                      color="black3"
                      padding={isDesktop ? '0 30px 0 37px' : '20px'}
                      variant="body2"
                    >
                      No ha sido posible obtener información de la institución. Por favor, te pedimos que lo vuelvas a
                      intentar dentro de unos minutos.
                    </Typography>
                  </ErrorMessageContainer>
                </GridItem>
              </ErrorContainer>
            )}
            {!error && loading && <ListParticipantsLoading />}
          </GridContainer>
        </GridItem>
      </ListParticipantsRootArea>
    </ProtectByOrgCode>
  )
}

export default ListParticipantsContainer
